import { InfoCircleOutlined } from '@ant-design/icons';
import { Card, Col, Form, Image, Input, Row, theme, Tooltip, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import PageSpinner from '../../../../shared/components/page-spinner/page-spinner';
import logo from '../../../../assets/logo.svg';
import { handleServerFormErrors } from '../../../../shared/utils/error-handling/error-handling';
import { useAppDispatch } from '../../../../store/hooks';
import { useGetAuthCredentialsMutation } from '../../api/auth-api';
import { AUTH_SCOPE } from '../../constants/auth-scope';
import { setAuthCredentials, setCodeVerifier, setDomain, setIsDomainPrefilled } from '../../store/auth-slice';
import { generateCodeChallenge } from '../../utils/generate-code-challenge';
import { generateCodeVerifier } from '../../utils/generate-code-verifier';
import styles from './login-form.module.css';
import CustomButton from '../../../../shared/components/buttons/custom-button/custom-button';

interface FormModel {
  domain: string;
}

const LoginForm = (): JSX.Element => {
  const { t } = useTranslation(['auth', 'common']);
  const [getAuthCredentials, { isLoading: isDomainLoading }] = useGetAuthCredentialsMutation();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const [showDomainForm, setShowDomainForm] = useState<boolean>(!searchParams.has('domain'));
  const [form] = Form.useForm<FormModel>();
  const { i18n } = useTranslation();

  const {
    token: { colorPrimary },
  } = theme.useToken();

  const handleGetAuthCredentials = (domain: string) => {
    getAuthCredentials(domain)
      .unwrap()
      .then(authCredentials => {
        const codeVerifier = generateCodeVerifier();
        const codeChallenge = generateCodeChallenge(codeVerifier);
        const params = new URLSearchParams();

        params.append('client_id', authCredentials.client_id);
        params.append('redirect_uri', `${window.location.origin}/oauth/callback`);
        params.append('response_type', 'code');
        params.append('scope', AUTH_SCOPE);
        params.append('code_challenge', codeChallenge);

        if (searchParams.has('domain')) {
          dispatch(setIsDomainPrefilled(true));
        }

        dispatch(setDomain(domain));
        dispatch(setCodeVerifier(codeVerifier));
        dispatch(setAuthCredentials(authCredentials));
        const locale = i18n.language.split('-')[0];

        window.location.assign(`${process.env.REACT_APP_API_URL}/oauth/v2/auth/${locale}?${params.toString()}`);
      })
      .catch(error => {
        setShowDomainForm(true);
        handleServerFormErrors(form, error.data?.errors || { domain: [t('login.message.invalidDomain')] });
      });
  };

  const handleFinish = ({ domain }: FormModel) => {
    handleGetAuthCredentials(domain);
  };

  useEffect(() => {
    dispatch(setIsDomainPrefilled(false));
    const domain = searchParams.get('domain');
    if (searchParams.has('domain') && domain) {
      handleGetAuthCredentials(domain);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  return (
    <>
      {!showDomainForm ? (
        <PageSpinner />
      ) : (
        <Row justify="center" align="middle" className={styles.loginCard}>
          <Col>
            <Card>
              <Row justify="center" gutter={[0, 16]}>
                <Col>
                  <Image src={logo} alt={t('login.labels.signontab')} height={76} preview={false} />
                </Col>
                <Col span={24} className={styles.loginText}>
                  <Typography.Text strong>{t('login.labels.logIn')}</Typography.Text>
                </Col>
              </Row>
              <Form form={form} layout="vertical" onFinish={handleFinish}>
                <Form.Item
                  label={
                    <span>
                      {t('label.domain', { ns: 'common' })}&nbsp;
                      <Tooltip title={t('login.labels.toolTip')} color={colorPrimary}>
                        <Typography.Text type="success">
                          <InfoCircleOutlined />
                        </Typography.Text>
                      </Tooltip>
                    </span>
                  }
                  name="domain"
                  rules={[{ required: true, message: t('errors.domainRequired', { ns: 'common' }) }]}
                >
                  <Input placeholder={t('login.labels.domainPlaceholder')} addonAfter={t('login.labels.sontDomain')} />
                </Form.Item>
                <Row justify="end">
                  <Col>
                    <Form.Item>
                      <CustomButton type="darkBlue" htmlType="submit" loading={isDomainLoading}>
                        {t('login.actions.next')}
                      </CustomButton>
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            </Card>
          </Col>
        </Row>
      )}
    </>
  );
};

export default LoginForm;
